// src/admin/shell/GlobalSearch.tsx
// Topbar command palette backed by public.global_search (migration
// 20260418000020, hardened in 21). Opens as a modal (Topbar owns the open
// state and the ⌘K / click trigger), debounces typing at 250ms, and routes
// hits to the right detail screen.
//
// Plan drift — documented inline:
//   - Plan placed this in src/admin/components/ with an *inline* input in
//     Topbar. Shipped Topbar uses a modal-palette UX (Search button + ⌘K
//     kbd) and already imports from './GlobalSearch'. We preserve the
//     shipped contract (`onClose` prop, keeps location).
//   - Plan's RPC shape used a `score` column; real migration returns
//     `match_score`, and adds a `kyc` kind. We handle both.
//   - Plan did not escape LIKE metachars — the real migration does via
//     public.like_escape, so wildcards in `q` are safe to pass through.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { supabase } from '@admin/lib/supabase';
import styles from './GlobalSearch.module.css';

type HitKind = 'loan' | 'borrower' | 'kyc' | 'admin';

interface Hit {
  kind: HitKind;
  id: string;
  label: string;
  sublabel: string | null;
  matchScore: number;
}

function isKnownKind(k: string | null): k is HitKind {
  return k === 'loan' || k === 'borrower' || k === 'kyc' || k === 'admin';
}

function hrefFor(h: Hit): string {
  switch (h.kind) {
    case 'loan':     return `/loans/${h.id}`;
    case 'borrower': return `/borrowers/${h.id}`;
    case 'kyc':      return `/kyc/${h.id}`;
    case 'admin':    return `/admins/${h.id}`;
  }
}

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

export default function GlobalSearch({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input on mount so the palette is immediately typeable.
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Esc closes the palette. Backdrop click is handled on the overlay div.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Debounced RPC.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = q.trim();
    if (trimmed.length < MIN_CHARS) { setHits([]); setErr(null); return; }
    timerRef.current = setTimeout(async () => {
      setBusy(true);
      setErr(null);
      try {
        const { data, error } = await supabase.rpc('global_search', { q: trimmed });
        if (error) throw error;
        const rows = (data ?? []).flatMap<Hit>((r) => {
          if (r.id === null || r.label === null || !isKnownKind(r.kind)) return [];
          return [{
            kind: r.kind,
            id: r.id,
            label: r.label,
            sublabel: r.sublabel,
            matchScore: r.match_score ?? 0,
          }];
        });
        setHits(rows);
      } catch (caught) {
        setErr(caught instanceof Error ? caught.message : 'Search failed');
        setHits([]);
      } finally {
        setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [q]);

  const showEmpty = !busy && !err && q.trim().length >= MIN_CHARS && hits.length === 0;
  const grouped = useMemo(() => groupByKind(hits), [hits]);

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-label="Global search">
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.inputRow}>
          <Search size={16} />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search loans, borrowers, KYC, admins…"
            aria-label="Search"
          />
          {busy && <span className={styles.busy} aria-hidden>…</span>}
        </div>
        <div className={styles.results} role="listbox">
          {err && <p className={styles.note} role="alert">{err}</p>}
          {!err && q.trim().length < MIN_CHARS && (
            <p className={styles.note}>Type at least {MIN_CHARS} characters to search.</p>
          )}
          {showEmpty && <p className={styles.note}>No matches.</p>}
          {grouped.map(([kind, items]) => (
            <section key={kind} className={styles.group}>
              <h3 className={styles.groupHead}>{labelFor(kind)}</h3>
              <ul>
                {items.map((h) => (
                  <li key={`${h.kind}:${h.id}`}>
                    <Link to={hrefFor(h)} onClick={onClose} className={styles.hit}>
                      <strong>{h.label}</strong>
                      {h.sublabel && <span className={styles.sub}>{h.sublabel}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function groupByKind(hits: Hit[]): [HitKind, Hit[]][] {
  const order: HitKind[] = ['loan', 'borrower', 'kyc', 'admin'];
  const by = new Map<HitKind, Hit[]>();
  for (const h of hits) {
    const list = by.get(h.kind) ?? [];
    list.push(h);
    by.set(h.kind, list);
  }
  return order.flatMap((k) => {
    const list = by.get(k);
    return list && list.length > 0 ? [[k, list] as [HitKind, Hit[]]] : [];
  });
}

function labelFor(k: HitKind): string {
  switch (k) {
    case 'loan':     return 'Loans';
    case 'borrower': return 'Borrowers';
    case 'kyc':      return 'KYC submissions';
    case 'admin':    return 'Admins';
  }
}
